import React, { useState, useEffect, useMemo } from 'react';
import {
  Trophy,
  Award,
  Settings,
  Search,
  Filter,
  Users,
  ChevronRight,
  ChevronLeft,
  ArrowUpRight,
  CheckCircle2,
  AlertCircle,
  Sparkles,
  TrendingUp,
  Medal,
  Crown,
  Building,
  RotateCcw,
  Sliders,
  ShieldCheck,
  UserCheck,
  FileText,
  Layers,
  BookOpen
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { PaidUjianCandidate, UserProfile } from '../../types';
import { GradingHeaderHero } from '../../components/grading/GradingHeaderHero';
import { getPaidUjianCandidatesDB, getMembersDB } from '../../services/db';
import { calculateGrade } from '../../data/ujianBertulisQuestions';
import {
  formatPublicName,
  isSuperAdmin,
  isUserManagement,
  isManagementOrTrainer,
  canViewMemberSensitiveInfo,
  getIcDetails
} from '../../utils/formatters';

export type ExamFacetType = 'all' | 'praktikal' | 'bertulis';

export interface RankedCandidate extends PaidUjianCandidate {
  // Active/Displayed metrics based on selectedExamFacet
  finalScore: number;
  finalGrade: string;
  isEvaluated: boolean;
  displayStatus: string;
  facetRank: number;

  gender: 'Lelaki' | 'Perempuan' | null;

  // Practical Examination (Fizikal Silibus)
  practicalScore: number;
  practicalGrade: string;
  isPracticalEvaluated: boolean;
  practicalStatus: 'Lulus' | 'Mengulang' | 'Belum Dinilai' | 'Sedang Dinilai';
  assessorCount: number;

  // Written Examination (Teori Digital 50 Soalan)
  writtenScore: number;
  writtenGrade: string;
  writtenRawScore: number;
  isWrittenCompleted: boolean;
  writtenStatus: 'Lulus' | 'Mengulang' | 'Belum Mengambil';
  writtenAttemptsCount: number;

  // Composite (Gabungan Praktikal 70% + Bertulis 30%)
  compositeScore: number;
  compositeGrade: string;
  isCompositeEvaluated: boolean;
  compositeStatus: string;
}

export const UjianGradingLeaderboardPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { showToast } = useToast();

  const canManageSettings = Boolean(currentUser && isManagementOrTrainer(currentUser));

  const [candidates, setCandidates] = useState<PaidUjianCandidate[]>([]);
  const [members, setMembers] = useState<UserProfile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Filters & Search
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [selectedExamFacet, setSelectedExamFacet] = useState<ExamFacetType>(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const facet = params.get('facet') || params.get('jenis');
      if (facet === 'praktikal') return 'praktikal';
      if (facet === 'teori' || facet === 'bertulis') return 'bertulis';
    }
    return 'all';
  });
  const [selectedGender, setSelectedGender] = useState<'all' | 'Lelaki' | 'Perempuan'>('all');
  const [selectedBengkung, setSelectedBengkung] = useState<string>('all');
  const [selectedGelanggang, setSelectedGelanggang] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');

  // Handle Exam Facet Change and synchronize with URL (?facet=praktikal / ?facet=teori)
  const handleSelectExamFacet = (facet: ExamFacetType) => {
    setSelectedExamFacet(facet);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (facet === 'all') {
        url.searchParams.delete('facet');
        url.searchParams.delete('jenis');
      } else if (facet === 'praktikal') {
        url.searchParams.set('facet', 'praktikal');
        url.searchParams.delete('jenis');
      } else if (facet === 'bertulis') {
        url.searchParams.set('facet', 'teori');
        url.searchParams.delete('jenis');
      }
      window.history.replaceState(null, '', url.pathname + (url.search ? url.search : ''));
    }
  };

  // Popstate listener for back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search);
      const facet = params.get('facet') || params.get('jenis');
      if (facet === 'praktikal') setSelectedExamFacet('praktikal');
      else if (facet === 'teori' || facet === 'bertulis') setSelectedExamFacet('bertulis');
      else setSelectedExamFacet('all');
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Load data
  const loadData = async () => {
    setIsLoading(true);
    try {
      const [candidatesData, membersData] = await Promise.all([
        getPaidUjianCandidatesDB(),
        getMembersDB()
      ]);
      setCandidates(candidatesData);
      setMembers(membersData);
    } catch (err: any) {
      console.error('Error loading leaderboard data:', err);
      showToast('error', 'Ralat Memuat Data', 'Gagal memuat senarai calon atau keputusan ujian.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    window.addEventListener('asmm_db_updated', loadData);
    return () => window.removeEventListener('asmm_db_updated', loadData);
  }, []);

  // Compute practical, written theory, and composite scores for each candidate
  const rankedCandidatesList: RankedCandidate[] = useMemo(() => {
    return candidates.map((c) => {
      // 1. Practical metrics
      const assessmentsList = c.assessments && c.assessments.length > 0
        ? c.assessments
        : c.assessment
        ? [c.assessment]
        : [];
      const assessorCount = assessmentsList.length;
      const isPracticalEvaluated = assessorCount > 0;
      let practicalScore = 0;
      let practicalGrade = 'Belum Dinilai';
      if (isPracticalEvaluated) {
        practicalScore = Math.round(
          assessmentsList.reduce((sum, a) => sum + (a.averageScore || 0), 0) / assessorCount
        );
        practicalGrade = calculateGrade(practicalScore);
      }
      const practicalStatus = c.gradingStatus;

      // 2. Written Theory metrics
      const isWrittenCompleted = Boolean(c.ujianBertulis && c.ujianBertulis.attemptsCount > 0);
      const writtenScore = isWrittenCompleted ? (c.ujianBertulis?.bestPercentage || 0) : 0;
      const writtenRawScore = isWrittenCompleted ? (c.ujianBertulis?.bestScore || 0) : 0;
      const writtenAttemptsCount = c.ujianBertulis?.attemptsCount || 0;
      const writtenGrade = isWrittenCompleted ? calculateGrade(writtenScore) : 'Belum Mengambil';
      const writtenStatus = c.ujianBertulis?.status || (isWrittenCompleted ? (c.ujianBertulis?.isPassed ? 'Lulus' : 'Mengulang') : 'Belum Mengambil');

      // 3. Composite / Overall metrics (70% Practical + 30% Written Theory)
      let compositeScore = 0;
      let compositeGrade = 'Belum Dinilai';
      let isCompositeEvaluated = false;
      let compositeStatus = 'Belum Dinilai';

      if (isPracticalEvaluated && isWrittenCompleted) {
        compositeScore = Math.round((practicalScore * 0.7) + (writtenScore * 0.3));
        compositeGrade = calculateGrade(compositeScore);
        isCompositeEvaluated = true;
        const bothPassed = practicalStatus === 'Lulus' && c.ujianBertulis?.isPassed;
        const anyFailed = practicalStatus === 'Mengulang' || c.ujianBertulis?.status === 'Mengulang';
        compositeStatus = bothPassed ? 'Lulus' : anyFailed ? 'Mengulang' : 'Sedang Dinilai';
      } else if (isPracticalEvaluated) {
        compositeScore = practicalScore;
        compositeGrade = practicalGrade;
        isCompositeEvaluated = true;
        compositeStatus = practicalStatus === 'Lulus' ? 'Lulus Praktikal' : practicalStatus;
      } else if (isWrittenCompleted) {
        compositeScore = writtenScore;
        compositeGrade = writtenGrade;
        isCompositeEvaluated = true;
        compositeStatus = c.ujianBertulis?.isPassed ? 'Lulus Teori' : 'Mengulang';
      }

      // Active score and grade according to selectedExamFacet
      let finalScore = compositeScore;
      let finalGrade = compositeGrade;
      let isEvaluated = isCompositeEvaluated;
      let displayStatus = compositeStatus;

      if (selectedExamFacet === 'praktikal') {
        finalScore = practicalScore;
        finalGrade = practicalGrade;
        isEvaluated = isPracticalEvaluated;
        displayStatus = practicalStatus;
      } else if (selectedExamFacet === 'bertulis') {
        finalScore = writtenScore;
        finalGrade = writtenGrade;
        isEvaluated = isWrittenCompleted;
        displayStatus = writtenStatus;
      }

      // Resolve gender from member profile or Malaysian IC
      const matched = members.find(
        (m) =>
          (c.memberId && m.id?.toLowerCase() === c.memberId.toLowerCase()) ||
          (c.memberIcNo && m.icNo && m.icNo.replace(/\D/g, '') === c.memberIcNo.replace(/\D/g, '')) ||
          (c.memberName && m.name?.toLowerCase() === c.memberName.toLowerCase())
      );
      let gender: 'Lelaki' | 'Perempuan' | null = ((matched as any)?.gender as any) || null;
      if (!gender) {
        const ic = c.memberIcNo || matched?.icNo;
        if (ic) {
          const icInfo = getIcDetails(ic);
          if (icInfo?.gender) gender = icInfo.gender;
        }
      }
      if (!gender) {
        const n = (c.memberName || '').toLowerCase();
        if (/\b(binti|bt\.|bte)\b/.test(n)) gender = 'Perempuan';
        else if (/\b(bin|ibn)\b/.test(n)) gender = 'Lelaki';
      }

      return {
        ...c,
        finalScore,
        finalGrade,
        isEvaluated,
        displayStatus,
        facetRank: 0,
        gender,
        practicalScore,
        practicalGrade,
        isPracticalEvaluated,
        practicalStatus,
        assessorCount,
        writtenScore,
        writtenGrade,
        writtenRawScore,
        isWrittenCompleted,
        writtenStatus,
        writtenAttemptsCount,
        compositeScore,
        compositeGrade,
        isCompositeEvaluated,
        compositeStatus
      };
    });
  }, [candidates, members, selectedExamFacet]);

  // Unique lists for filter pills
  const gelanggangOptions = useMemo(() => {
    const set = new Set<string>();
    candidates.forEach((c) => {
      if (c.gelanggang && c.gelanggang.trim()) set.add(c.gelanggang.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ms'));
  }, [candidates]);

  const bengkungOptions = useMemo(() => {
    const set = new Set<string>();
    candidates.forEach((c) => {
      if (c.targetBengkung && c.targetBengkung.trim()) set.add(c.targetBengkung.trim());
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'ms'));
  }, [candidates]);

  // Helper to compare candidates strictly by merit according to selected exam facet
  const compareByMerit = (a: RankedCandidate, b: RankedCandidate): number => {
    if (a.isEvaluated !== b.isEvaluated) {
      return a.isEvaluated ? -1 : 1;
    }
    if (b.finalScore !== a.finalScore) {
      return b.finalScore - a.finalScore;
    }
    if (selectedExamFacet === 'praktikal') {
      if (b.assessorCount !== a.assessorCount) {
        return b.assessorCount - a.assessorCount;
      }
    } else if (selectedExamFacet === 'bertulis') {
      if (b.writtenRawScore !== a.writtenRawScore) {
        return b.writtenRawScore - a.writtenRawScore;
      }
      if (a.writtenAttemptsCount !== b.writtenAttemptsCount) {
        return a.writtenAttemptsCount - b.writtenAttemptsCount;
      }
    } else {
      // In composite 'all', compare practical then written
      if (b.practicalScore !== a.practicalScore) {
        return b.practicalScore - a.practicalScore;
      }
      if (b.writtenScore !== a.writtenScore) {
        return b.writtenScore - a.writtenScore;
      }
    }
    return a.memberName.localeCompare(b.memberName, 'ms');
  };

  // Overall statistics for active facet
  const stats = useMemo(() => {
    const totalCandidates = rankedCandidatesList.length;
    const evaluated = rankedCandidatesList.filter((c) => c.isEvaluated);
    const evaluatedCount = evaluated.length;

    const practicalEvaluatedCount = rankedCandidatesList.filter((c) => c.isPracticalEvaluated).length;
    const writtenCompletedCount = rankedCandidatesList.filter((c) => c.isWrittenCompleted).length;

    const passCount = evaluated.filter((c) => c.finalScore >= 60).length;
    const cemerlangCount = evaluated.filter((c) => c.finalScore >= 85).length;
    const topScorer = evaluated.reduce<RankedCandidate | null>((top, current) => {
      if (!top) return current;
      return compareByMerit(current, top) < 0 ? current : top;
    }, null);

    const overallAvg = evaluatedCount > 0
      ? Math.round(evaluated.reduce((sum, c) => sum + c.finalScore, 0) / evaluatedCount)
      : 0;

    const passRate = evaluatedCount > 0 ? Math.round((passCount / evaluatedCount) * 100) : 0;

    return {
      totalCandidates,
      evaluatedCount,
      practicalEvaluatedCount,
      writtenCompletedCount,
      passCount,
      cemerlangCount,
      topScorer,
      overallAvg,
      passRate
    };
  }, [rankedCandidatesList, selectedExamFacet]);

  // STAGE 1: Faceted candidates (filtered by Facets: Gelanggang, Bengkung, Gender, Status)
  // Strictly sorted by merit, assigning each candidate their canonical facet rank (1, 2, 3...)
  const facetedCandidates: RankedCandidate[] = useMemo(() => {
    const list = rankedCandidatesList.filter((c) => {
      // 1. Gender facet
      if (selectedGender !== 'all' && c.gender !== selectedGender) {
        return false;
      }

      // 2. Bengkung level facet
      if (selectedBengkung !== 'all' && c.targetBengkung !== selectedBengkung) {
        return false;
      }

      // 3. Gelanggang facet
      if (selectedGelanggang !== 'all' && c.gelanggang !== selectedGelanggang) {
        return false;
      }

      // 4. Status facet (checked against active finalScore & isEvaluated)
      if (selectedStatus === 'cemerlang' && (c.finalScore < 85 || !c.isEvaluated)) return false;
      if (selectedStatus === 'lulus' && (c.finalScore < 60 || !c.isEvaluated)) return false;
      if (selectedStatus === 'mengulang' && (c.finalScore >= 60 || !c.isEvaluated)) return false;
      if (selectedStatus === 'belum' && c.isEvaluated) return false;

      return true;
    });

    // Strictly sort by merit (score -> consensus assessors -> alphabetical name)
    list.sort(compareByMerit);

    // Assign permanent canonical rank within this facet
    return list.map((cand, idx) => ({
      ...cand,
      facetRank: idx + 1
    }));
  }, [rankedCandidatesList, selectedGender, selectedBengkung, selectedGelanggang, selectedStatus, selectedExamFacet]);

  // STAGE 2: Top 3 Podium of Excellence (derived strictly from top evaluated of current facet)
  const topPodium = useMemo(() => {
    const evaluated = facetedCandidates.filter((c) => c.isEvaluated && c.finalScore > 0);
    return {
      first: evaluated[0] || null,
      second: evaluated[1] || null,
      third: evaluated[2] || null
    };
  }, [facetedCandidates]);

  // STAGE 3: Search within faceted pool without corrupting or resetting the facetRank
  const displayCandidates = useMemo(() => {
    if (!searchQuery.trim()) {
      return facetedCandidates;
    }
    const q = searchQuery.toLowerCase().trim();
    return facetedCandidates.filter((c) => {
      const matchName = c.memberName.toLowerCase().includes(q);
      const matchId = c.memberId.toLowerCase().includes(q);
      const canViewSensitive = currentUser && canViewMemberSensitiveInfo(currentUser, c as any);
      const matchIc = canViewSensitive && (c.memberIcNo || '').toLowerCase().includes(q);
      return matchName || matchId || matchIc;
    });
  }, [facetedCandidates, searchQuery, currentUser]);

  const handleResetFilters = () => {
    setSearchQuery('');
    setSelectedGender('all');
    setSelectedBengkung('all');
    setSelectedGelanggang('all');
    setSelectedStatus('all');
  };

  const hasActiveFilters = Boolean(
    searchQuery.trim() ||
    selectedGender !== 'all' ||
    selectedBengkung !== 'all' ||
    selectedGelanggang !== 'all' ||
    selectedStatus !== 'all'
  );

  // Helper to resolve display photo
  const getCandidatePhoto = (cand: RankedCandidate): string => {
    const matched = members.find(
      (m) =>
        (cand.memberId && m.id.toLowerCase() === cand.memberId.toLowerCase()) ||
        (cand.memberIcNo && m.icNo === cand.memberIcNo) ||
        (cand.memberName && m.name.toLowerCase() === cand.memberName.toLowerCase())
    );
    const photo = matched?.photoUrl || cand.photoUrl;
    if (photo && !photo.includes('/badges/')) {
      return photo;
    }
    return '/default-avatar.svg';
  };

  const handleNavigateToCandidateGrading = (memberId: string) => {
    const slug = memberId.toLowerCase().trim();
    window.history.pushState(null, '', `/grading/${slug}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  return (
    <div className="bg-white min-h-screen text-slate-900 pb-16">
      {/* 1. STANDARDIZED TOP HERO & LEADERBOARD HEADER CARD */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <GradingHeaderHero
          activeTab="leaderboard"
          badgeTag="Papan Skor & Kedudukan Rasmi Ujian Bengkung ASMM"
          badgeIcon={Trophy}
          badgeColorClass="bg-amber-50 text-amber-800 border-amber-200/80"
          title="Papan Kedudukan Calon Ujian Grading"
          subtitle="Penarafan prestasi dan kedudukan merit calon merangkumi kedua-dua komponen: Ujian Praktikal (Fizikal Silibus) dan Ujian Bertulis (Teori Silat & Sahsiah)."
          metrics={[
            {
              value: `${stats.evaluatedCount} / ${stats.totalCandidates}`,
              label: selectedExamFacet === 'all' ? 'Calon Selesai' : selectedExamFacet === 'praktikal' ? 'Dinilai Panel' : 'Mengambil Ujian',
              colorClass: 'text-slate-900'
            },
            {
              value: `${stats.overallAvg}%`,
              label: selectedExamFacet === 'all' ? 'Purata Komposit' : selectedExamFacet === 'praktikal' ? 'Purata Praktikal' : 'Purata Bertulis',
              colorClass: 'text-blue-600'
            },
            {
              value: stats.topScorer ? `${stats.topScorer.finalScore}%` : '-',
              label: 'Markah Tertinggi',
              colorClass: 'text-amber-600'
            },
            {
              value: `${stats.passRate}%`,
              label: 'Kadar Kelulusan',
              colorClass: 'text-red-600'
            },
          ]}
        >
          {/* 3 Facet Selector: Semua (Komposit), Praktikal, Teori */}
          <div className="flex items-center justify-center">
            <div className="inline-flex items-center p-1.5 rounded-2xl bg-slate-100 border border-slate-200/90 gap-1.5 max-w-full overflow-x-auto shadow-2xs">
              {/* 1. Komposit / Semua */}
              <button
                type="button"
                onClick={() => handleSelectExamFacet('all')}
                className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-2 whitespace-nowrap ${
                  selectedExamFacet === 'all'
                    ? 'bg-slate-900 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'
                }`}
              >
                <Layers className={`w-3.5 h-3.5 ${selectedExamFacet === 'all' ? 'text-amber-400' : 'text-slate-500'}`} />
                <span>Semua (Komposit 70/30)</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  selectedExamFacet === 'all' ? 'bg-slate-800 text-slate-200' : 'bg-slate-200 text-slate-700'
                }`}>
                  {stats.evaluatedCount}
                </span>
              </button>

              {/* 2. Praktikal */}
              <button
                type="button"
                onClick={() => handleSelectExamFacet('praktikal')}
                className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-2 whitespace-nowrap ${
                  selectedExamFacet === 'praktikal'
                    ? 'bg-red-600 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'
                }`}
              >
                <ShieldCheck className="w-3.5 h-3.5 text-white" />
                <span>Ujian Praktikal (Fizikal)</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  selectedExamFacet === 'praktikal' ? 'bg-red-700 text-white' : 'bg-slate-200 text-slate-700'
                }`}>
                  {stats.practicalEvaluatedCount}
                </span>
              </button>

              {/* 3. Teori */}
              <button
                type="button"
                onClick={() => handleSelectExamFacet('bertulis')}
                className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-2 whitespace-nowrap ${
                  selectedExamFacet === 'bertulis'
                    ? 'bg-blue-600 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900 hover:bg-white/70'
                }`}
              >
                <FileText className="w-3.5 h-3.5 text-white" />
                <span>Ujian Teori (Bertulis)</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                  selectedExamFacet === 'bertulis' ? 'bg-blue-700 text-white' : 'bg-slate-200 text-slate-700'
                }`}>
                  {stats.writtenCompletedCount}
                </span>
              </button>
            </div>
          </div>
        </GradingHeaderHero>
      </div>

      {/* 2. TOP 3 PODIUM OF EXCELLENCE */}
      {topPodium.first && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 mb-8">
          <div className="text-center mb-4">
            <h2 className="text-xs font-black uppercase tracking-wider text-slate-400 flex items-center justify-center gap-1.5">
              <Crown className="w-4 h-4 text-amber-500" />
              <span>
                Kedudukan Terbaik{' '}
                {selectedExamFacet === 'all'
                  ? 'Keseluruhan (Praktikal & Teori)'
                  : selectedExamFacet === 'praktikal'
                  ? 'Ujian Praktikal (Fizikal Silibus)'
                  : 'Ujian Bertulis (Teori Silat)'}
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end pt-4">
            {/* 2ND PLACE (SILVER / LAUTAN BIRU) */}
            {topPodium.second && (
              <div
                onClick={() => handleNavigateToCandidateGrading(topPodium.second!.memberId)}
                className="bg-white rounded-3xl border-2 border-slate-200 hover:border-blue-400 p-5 text-center shadow-xs transition hover:shadow-md cursor-pointer relative order-2 md:order-1 group"
              >
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-slate-200 text-slate-800 text-[10px] font-black uppercase tracking-wider flex items-center gap-1 shadow-2xs">
                  <Medal className="w-3 h-3 text-slate-600" />
                  <span>Tempat Ke-2</span>
                </div>

                <div className="w-16 h-16 rounded-full ring-4 ring-slate-100 mx-auto mt-2 overflow-hidden bg-slate-50">
                  <img
                    src={getCandidatePhoto(topPodium.second)}
                    alt={topPodium.second.memberName}
                    className="w-full h-full object-cover"
                  />
                </div>

                <h3 className="font-extrabold text-slate-900 text-sm mt-3 truncate group-hover:text-blue-600 transition">
                  {formatPublicName(topPodium.second.memberName)}
                </h3>
                <div className="text-[11px] text-slate-500 font-medium">
                  {topPodium.second.gelanggang}
                </div>

                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-blue-50 text-blue-700 font-black text-sm">
                  <span>{topPodium.second.finalScore}%</span>
                  <span className="text-[10px] font-bold text-blue-500">• {topPodium.second.finalGrade}</span>
                </div>

                <div className="mt-2 text-[10px] font-bold text-slate-500 flex items-center justify-center gap-1.5 flex-wrap">
                  <span>Praktikal: <strong className="text-slate-800">{topPodium.second.isPracticalEvaluated ? `${topPodium.second.practicalScore}%` : '-'}</strong></span>
                  <span>•</span>
                  <span>Teori: <strong className="text-slate-800">{topPodium.second.isWrittenCompleted ? `${topPodium.second.writtenScore}%` : '-'}</strong></span>
                </div>
              </div>
            )}

            {/* 1ST PLACE (GOLD / KUNING DIRAJA CENTERPIECE) */}
            <div
              onClick={() => handleNavigateToCandidateGrading(topPodium.first.memberId)}
              className="bg-white rounded-3xl border-2 border-amber-400 p-6 text-center shadow-md transition hover:shadow-lg cursor-pointer relative order-1 md:order-2 group ring-4 ring-amber-100/60"
            >
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-amber-500 text-white text-[11px] font-black uppercase tracking-wider flex items-center gap-1.5 shadow-sm">
                <Crown className="w-3.5 h-3.5 text-white animate-pulse" />
                <span>
                  {selectedExamFacet === 'all'
                    ? 'JUARA KESELURUHAN #1'
                    : selectedExamFacet === 'praktikal'
                    ? 'TERBAIK PRAKTIKAL #1'
                    : 'TERBAIK TEORI #1'}
                </span>
              </div>

              <div className="w-20 h-20 rounded-full ring-4 ring-amber-200 mx-auto mt-2 overflow-hidden bg-amber-50 shadow-xs">
                <img
                  src={getCandidatePhoto(topPodium.first)}
                  alt={topPodium.first.memberName}
                  className="w-full h-full object-cover"
                />
              </div>

              <h3 className="font-extrabold text-slate-900 text-base sm:text-lg mt-3 truncate group-hover:text-amber-600 transition">
                {formatPublicName(topPodium.first.memberName)}
              </h3>
              <div className="text-xs text-slate-500 font-medium">
                {topPodium.first.gelanggang}
              </div>

              <div className="mt-3 inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl bg-amber-50 text-amber-900 font-black text-base border border-amber-200 shadow-2xs">
                <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
                <span>{topPodium.first.finalScore}%</span>
                <span className="text-xs font-extrabold text-amber-700">({topPodium.first.finalGrade})</span>
              </div>

              <div className="mt-2 text-[11px] font-bold text-slate-600 flex items-center justify-center gap-2 flex-wrap">
                <span>Praktikal: <strong className="text-slate-900">{topPodium.first.isPracticalEvaluated ? `${topPodium.first.practicalScore}%` : '-'}</strong></span>
                <span>•</span>
                <span>Teori: <strong className="text-slate-900">{topPodium.first.isWrittenCompleted ? `${topPodium.first.writtenScore}%` : '-'}</strong></span>
              </div>
            </div>

            {/* 3RD PLACE (BRONZE / MERAH MUDA) */}
            {topPodium.third && (
              <div
                onClick={() => handleNavigateToCandidateGrading(topPodium.third!.memberId)}
                className="bg-white rounded-3xl border-2 border-slate-200 hover:border-red-400 p-5 text-center shadow-xs transition hover:shadow-md cursor-pointer relative order-3 group"
              >
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-red-100 text-red-800 text-[10px] font-black uppercase tracking-wider flex items-center gap-1 shadow-2xs">
                  <Medal className="w-3 h-3 text-red-600" />
                  <span>Tempat Ke-3</span>
                </div>

                <div className="w-16 h-16 rounded-full ring-4 ring-slate-100 mx-auto mt-2 overflow-hidden bg-slate-50">
                  <img
                    src={getCandidatePhoto(topPodium.third)}
                    alt={topPodium.third.memberName}
                    className="w-full h-full object-cover"
                  />
                </div>

                <h3 className="font-extrabold text-slate-900 text-sm mt-3 truncate group-hover:text-red-600 transition">
                  {formatPublicName(topPodium.third.memberName)}
                </h3>
                <div className="text-[11px] text-slate-500 font-medium">
                  {topPodium.third.gelanggang}
                </div>

                <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-red-50 text-red-700 font-black text-sm">
                  <span>{topPodium.third.finalScore}%</span>
                  <span className="text-[10px] font-bold text-red-500">• {topPodium.third.finalGrade}</span>
                </div>

                <div className="mt-2 text-[10px] font-bold text-slate-500 flex items-center justify-center gap-1.5 flex-wrap">
                  <span>Praktikal: <strong className="text-slate-800">{topPodium.third.isPracticalEvaluated ? `${topPodium.third.practicalScore}%` : '-'}</strong></span>
                  <span>•</span>
                  <span>Teori: <strong className="text-slate-800">{topPodium.third.isWrittenCompleted ? `${topPodium.third.writtenScore}%` : '-'}</strong></span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* 3. INTERACTIVE SEARCH & FILTER CONTROLS */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 mb-6">
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-xs space-y-3">
          {/* Top Search Input Bar (No sort dropdown - strictly by merit) */}
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                placeholder="Cari nama calon, ID pesilat, atau No. IC..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 sm:py-2.5 text-xs font-medium rounded-xl border border-slate-200 focus:outline-none focus:border-red-600 bg-slate-50/50 transition"
              />
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleResetFilters}
                className="inline-flex items-center gap-1.5 px-3 py-2 sm:py-2.5 rounded-xl text-xs font-bold text-slate-600 hover:text-red-600 hover:bg-red-50 border border-slate-200 hover:border-red-200 transition shrink-0 cursor-pointer"
                title="Set Semula Semua Tapisan"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Set Semula</span>
              </button>
            )}
          </div>

          {/* Facets Filter Section */}
          <div className="space-y-3 pt-3 border-t border-slate-100 text-xs">
            {/* Facet 0: Komponen Ujian (Praktikal vs Teori vs Komposit) */}
            <div className="flex items-center gap-1.5 flex-wrap pb-2 border-b border-slate-100/80">
              <span className="text-slate-500 font-bold text-[11px] mr-1 flex items-center gap-1">
                <Layers className="w-3.5 h-3.5 text-slate-400" />
                Komponen Ujian:
              </span>
              <button
                type="button"
                onClick={() => handleSelectExamFacet('all')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                  selectedExamFacet === 'all'
                    ? 'bg-slate-900 text-white shadow-2xs font-black'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                <span>Semua (Komposit)</span>
                <span className="text-[10px] opacity-75">({stats.evaluatedCount})</span>
              </button>
              <button
                type="button"
                onClick={() => handleSelectExamFacet('praktikal')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                  selectedExamFacet === 'praktikal'
                    ? 'bg-red-600 text-white shadow-2xs font-black'
                    : 'bg-red-50 text-red-800 hover:bg-red-100'
                }`}
              >
                <ShieldCheck className="w-3 h-3" />
                <span>Praktikal</span>
                <span className="text-[10px] opacity-75">({stats.practicalEvaluatedCount})</span>
              </button>
              <button
                type="button"
                onClick={() => handleSelectExamFacet('bertulis')}
                className={`px-3 py-1 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                  selectedExamFacet === 'bertulis'
                    ? 'bg-blue-600 text-white shadow-2xs font-black'
                    : 'bg-blue-50 text-blue-800 hover:bg-blue-100'
                }`}
              >
                <FileText className="w-3 h-3" />
                <span>Teori (Bertulis)</span>
                <span className="text-[10px] opacity-75">({stats.writtenCompletedCount})</span>
              </button>
            </div>

            {/* Facet 1: Jantina & Gelanggang */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between flex-wrap">
              {/* Jantina Facet */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-slate-400 font-bold text-[11px] mr-1 flex items-center gap-1">
                  <Users className="w-3 h-3 text-slate-400" />
                  Jantina:
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedGender('all')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                    selectedGender === 'all'
                      ? 'bg-slate-900 text-white shadow-2xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Semua
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedGender('Lelaki')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                    selectedGender === 'Lelaki'
                      ? 'bg-blue-600 text-white shadow-2xs'
                      : 'bg-blue-50 text-blue-800 hover:bg-blue-100'
                  }`}
                >
                  Lelaki
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedGender('Perempuan')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                    selectedGender === 'Perempuan'
                      ? 'bg-rose-600 text-white shadow-2xs'
                      : 'bg-rose-50 text-rose-800 hover:bg-rose-100'
                  }`}
                >
                  Perempuan
                </button>
              </div>

              {/* Gelanggang Facet */}
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-slate-400 font-bold text-[11px] mr-1 flex items-center gap-1">
                  <Building className="w-3 h-3 text-slate-400" />
                  Gelanggang:
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedGelanggang('all')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                    selectedGelanggang === 'all'
                      ? 'bg-slate-900 text-white shadow-2xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Semua
                </button>
                {gelanggangOptions.map((g) => (
                  <button
                    key={g}
                    type="button"
                    onClick={() => setSelectedGelanggang(g)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                      selectedGelanggang === g
                        ? 'bg-slate-900 text-white shadow-2xs'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {g}
                  </button>
                ))}
              </div>
            </div>

            {/* Facet 2: Tahap Bengkung */}
            {bengkungOptions.length > 0 && (
              <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-slate-100/80">
                <span className="text-slate-400 font-bold text-[11px] mr-1 flex items-center gap-1">
                  <Award className="w-3 h-3 text-slate-400" />
                  Tahap Bengkung:
                </span>
                <button
                  type="button"
                  onClick={() => setSelectedBengkung('all')}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                    selectedBengkung === 'all'
                      ? 'bg-slate-900 text-white shadow-2xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  Semua
                </button>
                {bengkungOptions.map((b) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => setSelectedBengkung(b)}
                    className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                      selectedBengkung === b
                        ? 'bg-slate-900 text-white shadow-2xs'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            )}

            {/* Facet 3: Status Keputusan */}
            <div className="flex items-center gap-1.5 flex-wrap pt-2 border-t border-slate-100/80">
              <span className="text-slate-400 font-bold text-[11px] mr-1 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3 text-slate-400" />
                Status:
              </span>
              <button
                type="button"
                onClick={() => setSelectedStatus('all')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                  selectedStatus === 'all'
                    ? 'bg-slate-900 text-white shadow-2xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Semua
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('cemerlang')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                  selectedStatus === 'cemerlang'
                    ? 'bg-amber-500 text-white shadow-2xs'
                    : 'bg-amber-50 text-amber-800 hover:bg-amber-100'
                }`}
              >
                Cemerlang (A)
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('lulus')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                  selectedStatus === 'lulus'
                    ? 'bg-blue-600 text-white shadow-2xs'
                    : 'bg-blue-50 text-blue-800 hover:bg-blue-100'
                }`}
              >
                Lulus
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('mengulang')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                  selectedStatus === 'mengulang'
                    ? 'bg-red-600 text-white shadow-2xs'
                    : 'bg-red-50 text-red-800 hover:bg-red-100'
                }`}
              >
                Mengulang
              </button>
              <button
                type="button"
                onClick={() => setSelectedStatus('belum')}
                className={`px-2.5 py-1 rounded-lg text-xs font-bold transition cursor-pointer ${
                  selectedStatus === 'belum'
                    ? 'bg-slate-600 text-white shadow-2xs'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                Belum Dinilai
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 4. FULL LEADERBOARD CANDIDATE CARDS */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        {searchQuery.trim() && (
          <div className="mb-3 px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl flex items-center justify-between text-xs text-slate-600 font-bold">
            <span>
              Menunjukkan {displayCandidates.length} padanan carian untuk &ldquo;{searchQuery}&rdquo; (Kedudukan rasmi dikekalkan mengikut tapisan kategori)
            </span>
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="text-red-600 hover:underline cursor-pointer"
            >
              Kosongkan carian
            </button>
          </div>
        )}

        <div className="space-y-3">
          {displayCandidates.length === 0 ? (
            <div className="bg-white rounded-3xl border border-slate-200 p-8 sm:p-12 text-center shadow-xs">
              <Trophy className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <h3 className="text-base font-bold text-slate-800">Tiada Calon Ditemui</h3>
              <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                Tiada rekod calon ujian grading yang menepati kriteria carian atau tapisan yang dipilih.
              </p>
            </div>
          ) : (
            displayCandidates.map((cand) => {
              const rank = cand.facetRank;
              const isTopThree = rank <= 3 && cand.isEvaluated && cand.finalScore > 0;
              const isPassed = cand.gradingStatus === 'Lulus';

              return (
                <div
                  key={cand.id || cand.memberId}
                  onClick={() => handleNavigateToCandidateGrading(cand.memberId)}
                  className={`bg-white rounded-2xl border p-4 sm:p-5 transition hover:shadow-md cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-4 group ${
                    isTopThree
                      ? rank === 1
                        ? 'border-amber-300 ring-2 ring-amber-100/70 shadow-xs'
                        : rank === 2
                        ? 'border-blue-200 shadow-xs'
                        : 'border-red-200 shadow-xs'
                      : 'border-slate-200 hover:border-slate-300 shadow-2xs'
                  }`}
                >
                  {/* Left: Rank Badge + Candidate Info */}
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    {/* Rank Badge Indicator */}
                    <div
                      className={`w-9 h-9 sm:w-11 sm:h-11 rounded-2xl flex items-center justify-center font-black text-xs sm:text-sm shrink-0 shadow-2xs ${
                        rank === 1 && cand.isEvaluated
                          ? 'bg-amber-500 text-white'
                          : rank === 2 && cand.isEvaluated
                          ? 'bg-blue-600 text-white'
                          : rank === 3 && cand.isEvaluated
                          ? 'bg-red-600 text-white'
                          : 'bg-slate-100 text-slate-700 border border-slate-200'
                      }`}
                    >
                      {rank === 1 && cand.isEvaluated ? (
                        <Crown className="w-5 h-5 text-white" />
                      ) : (
                        `#${rank}`
                      )}
                    </div>

                    {/* Candidate Avatar */}
                    <div className="w-12 h-12 rounded-full overflow-hidden bg-slate-100 ring-2 ring-slate-100 shrink-0">
                      <img
                        src={getCandidatePhoto(cand)}
                        alt={cand.memberName}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Names & Gelanggang */}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                          {cand.memberId}
                        </span>
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-red-50 text-red-700 border border-red-200">
                          {cand.gelanggang}
                        </span>
                        {cand.gender && (
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                              cand.gender === 'Lelaki'
                                ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                : 'bg-rose-50 text-rose-700 border border-rose-200'
                            }`}
                          >
                            {cand.gender}
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm sm:text-base font-extrabold text-slate-900 truncate mt-0.5 group-hover:text-red-600 transition">
                        {formatPublicName(cand.memberName)}
                      </h4>
                      <div className="text-[11px] text-slate-500 font-medium">
                        Ujian: <strong className="text-slate-800">{cand.targetBengkung}</strong>
                      </div>
                    </div>
                  </div>

                  {/* Middle-Right: Both Exam Scores Breakdown + Active Metric */}
                  <div className="flex flex-wrap items-center justify-between md:justify-end gap-3 sm:gap-5 pt-3 md:pt-0 border-t md:border-t-0 border-slate-100 shrink-0">
                    {/* Dual Exam Breakdown Badges */}
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {/* Practical Score Pill */}
                      <span className={`text-[10px] sm:text-[11px] font-bold px-2.5 py-1 rounded-xl border flex items-center gap-1 ${
                        selectedExamFacet === 'praktikal'
                          ? 'bg-red-50 text-red-900 border-red-300 ring-1 ring-red-300 shadow-2xs'
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                      }`}>
                        <ShieldCheck className="w-3 h-3 text-red-600 shrink-0" />
                        <span>Praktikal: </span>
                        <strong className="text-slate-900">{cand.isPracticalEvaluated ? `${cand.practicalScore}%` : 'Belum'}</strong>
                        {cand.isPracticalEvaluated && <span className="text-[9px] text-slate-400">({cand.assessorCount}J)</span>}
                      </span>

                      {/* Written Theory Score Pill */}
                      <span className={`text-[10px] sm:text-[11px] font-bold px-2.5 py-1 rounded-xl border flex items-center gap-1 ${
                        selectedExamFacet === 'bertulis'
                          ? 'bg-blue-50 text-blue-900 border-blue-300 ring-1 ring-blue-300 shadow-2xs'
                          : 'bg-slate-50 text-slate-700 border-slate-200'
                      }`}>
                        <FileText className="w-3 h-3 text-blue-600 shrink-0" />
                        <span>Teori: </span>
                        <strong className="text-slate-900">{cand.isWrittenCompleted ? `${cand.writtenScore}%` : 'Belum'}</strong>
                        {cand.isWrittenCompleted && <span className="text-[9px] text-slate-400">({cand.writtenRawScore}/50)</span>}
                      </span>
                    </div>

                    {/* Active Score Bar & Percentage for current Facet */}
                    <div className="text-left md:text-right min-w-[70px]">
                      {cand.isEvaluated ? (
                        <>
                          <div className="flex items-center md:justify-end gap-1.5">
                            <span className="text-base sm:text-xl font-black text-slate-900">
                              {cand.finalScore}%
                            </span>
                            <span
                              className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                                cand.finalScore >= 85
                                  ? 'bg-amber-100 text-amber-800'
                                  : cand.finalScore >= 70
                                  ? 'bg-blue-100 text-blue-800'
                                  : cand.finalScore >= 60
                                  ? 'bg-slate-100 text-slate-800'
                                  : 'bg-red-100 text-red-800'
                              }`}
                            >
                              {cand.finalGrade}
                            </span>
                          </div>
                          <div className="text-[10px] text-slate-400 font-bold">
                            {selectedExamFacet === 'all'
                              ? 'Skor Komposit'
                              : selectedExamFacet === 'praktikal'
                              ? `${cand.assessorCount} Penilai`
                              : `${cand.writtenRawScore}/50 Soalan`}
                          </div>
                        </>
                      ) : (
                        <div className="text-xs font-bold text-slate-400 bg-slate-100 px-3 py-1 rounded-xl">
                          Belum Lengkap
                        </div>
                      )}
                    </div>

                    {/* Active Status Badge */}
                    <div className="hidden sm:block min-w-[95px] text-right">
                      <span
                        className={`text-xs font-extrabold px-3 py-1.5 rounded-xl border inline-flex items-center gap-1.5 ${
                          cand.finalScore >= 60 && cand.isEvaluated
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : cand.isEvaluated
                            ? 'bg-red-50 text-red-800 border-red-200'
                            : 'bg-slate-50 text-slate-600 border-slate-200'
                        }`}
                      >
                        {cand.finalScore >= 60 && cand.isEvaluated ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                        ) : cand.isEvaluated ? (
                          <AlertCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />
                        ) : null}
                        <span>{cand.displayStatus.toUpperCase()}</span>
                      </span>
                    </div>

                    {/* Action Arrow Link */}
                    <div className="w-8 h-8 rounded-xl bg-slate-50 group-hover:bg-slate-900 group-hover:text-white flex items-center justify-center text-slate-400 transition shrink-0">
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
